// src/context/PartyVitalsContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { rtdb } from '../firebase/config';
import { ref, onValue, set, onDisconnect, serverTimestamp } from 'firebase/database';
import { useAuth } from './AuthContext';

const PartyVitalsContext = createContext();

/**
 * Hook to access party vitals data
 * Must be used within a PartyVitalsProvider
 */
export const usePartyVitals = () => {
  const context = useContext(PartyVitalsContext);
  if (!context) {
    throw new Error('usePartyVitals must be used within a PartyVitalsProvider');
  }
  return context;
};

/**
 * Provider for real-time party vitals synchronization
 * Tracks HP, Stress, Hope, and online status for all party members
 *
 * @param {string} campaignId - Campaign ID for RTDB path
 * @param {Object} character - Current character data (to publish)
 * @param {React.ReactNode} children - Child components
 */
export const PartyVitalsProvider = ({ campaignId, character, children }) => {
  const { user } = useAuth();
  const [partyMembers, setPartyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Subscribe to all party vitals
  useEffect(() => {
    if (!campaignId) {
      setLoading(false);
      return;
    }

    const vitalsRef = ref(rtdb, `campaigns/${campaignId}/partyVitals`);

    const unsubscribe = onValue(vitalsRef, (snapshot) => {
      const vitalsData = snapshot.val();

      if (vitalsData) {
        const membersList = Object.entries(vitalsData).map(([characterId, data]) => ({
          characterId,
          ...data
        }));
        setPartyMembers(membersList);
      } else {
        setPartyMembers([]);
      }
      setLoading(false);
    }, (error) => {
      console.error('[PartyVitals] Error subscribing to vitals:', error);
      setError(error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [campaignId]);

  // Publish current character's vitals when they change
  useEffect(() => {
    if (!campaignId || !character?.id || !user) {
      return;
    }

    const characterVitalsRef = ref(rtdb, `campaigns/${campaignId}/partyVitals/${character.id}`);

    const vitalsData = {
      name: character.name || 'Unknown',
      hp: character.hp ?? 0,
      maxHp: character.maxHp ?? 8,
      stress: character.stress ?? 0,
      maxStress: character.maxStress ?? 8,
      hope: character.hope ?? 0,
      maxHope: 6 - (character.scars?.length || 0),
      armor: character.armor ?? 0,
      temporaryArmor: character.temporaryArmor || null,
      characterId: character.id,
      userId: user.uid,
      status: 'online',
      lastUpdated: serverTimestamp()
    };

    // Publish vitals
    set(characterVitalsRef, vitalsData)
      .catch((error) => {
        console.error('[PartyVitals] Error publishing vitals:', error);
      });

    // Set up disconnect cleanup - mark as offline when disconnecting
    const disconnectRef = ref(rtdb, `campaigns/${campaignId}/partyVitals/${character.id}/status`);
    onDisconnect(disconnectRef).set('offline')
      .catch((error) => {
        console.error('[PartyVitals] Error setting disconnect cleanup:', error);
      });

  }, [
    campaignId,
    character?.id,
    character?.name,
    character?.hp,
    character?.maxHp,
    character?.stress,
    character?.maxStress,
    character?.hope,
    character?.armor,
    character?.scars?.length,
    user
  ]);

  // Get allies (party members excluding current character)
  const getAllies = useCallback(() => {
    if (!character?.id) return partyMembers;
    return partyMembers.filter(member => member.characterId !== character.id);
  }, [partyMembers, character?.id]);

  // Get online allies only
  const getOnlineAllies = useCallback(() => {
    return getAllies().filter(member => member.status === 'online');
  }, [getAllies]);

  // Get a specific party member by characterId
  const getPartyMember = useCallback((characterId) => {
    return partyMembers.find(member => member.characterId === characterId);
  }, [partyMembers]);

  // Check if we're the only one in the party (for fallback UI)
  const isSoloPlay = partyMembers.length <= 1;

  const value = {
    // Data
    partyMembers,
    loading,
    error,

    // Helpers
    getAllies,
    getOnlineAllies,
    getPartyMember,
    isSoloPlay,

    // Meta
    currentCharacterId: character?.id
  };

  return (
    <PartyVitalsContext.Provider value={value}>
      {children}
    </PartyVitalsContext.Provider>
  );
};

export default PartyVitalsContext;
